import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import {
  Container,
  Paper,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Button,
  Chip,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Rating,
  Alert,
  CircularProgress,
  Divider,
  Avatar
} from '@mui/material';
import {
  CheckCircle,
  HourglassEmpty,
  Assignment,
  LocationOn,
  CalendarToday,
  Person,
  AttachMoney,
  Info,
  Star,
  Message,
  Cancel,
  Visibility
} from '@mui/icons-material';
import { formatDistanceToNow } from 'date-fns';
import VerifierLocationTracker from '../components/VerifierLocationTracker';
import DialogCloseButton from '../components/common/DialogCloseButton';

const ClientVerificationRequestsPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [requests, setRequests] = useState([]);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [approvalDialog, setApprovalDialog] = useState(false);
  const [rating, setRating] = useState(5);
  const [comments, setComments] = useState('');
  const [error, setError] = useState(null);
  const [withdrawDialog, setWithdrawDialog] = useState(false);
  const [withdrawing, setWithdrawing] = useState(false);
  const [typeFilter, setTypeFilter] = useState('all'); // all | property | project

  useEffect(() => {
    loadVerificationRequests();
  }, [user]);

  const loadVerificationRequests = async () => {
    try {
      setLoading(true);
      const response = await api.get('/verification-requests', {
        params: { clientId: user?.id }
      });
      setRequests(response.data.data || []);
    } catch (err) {
      console.error('Error loading verification requests:', err);
      setError('Failed to load verification requests');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async () => {
    try {
      await api.post(
        `/verification-requests/${selectedRequest.id}/approval`,
        {
          approverRole: 'client',
          status: 'approved',
          rating,
          comments
        }
      );
      
      setApprovalDialog(false);
      loadVerificationRequests();
    } catch (err) {
      console.error('Error approving verification:', err);
      setError('Failed to approve verification');
    }
  };

  const handleWithdraw = async () => {
    try {
      setWithdrawing(true);
      await api.post(`/verification-requests/${selectedRequest.id}/withdraw`);
      setWithdrawDialog(false);
      setSelectedRequest(null);
      loadVerificationRequests();
    } catch (err) {
      console.error('Error withdrawing verification:', err);
      setError(err.response?.data?.message || 'Failed to withdraw verification request');
    } finally {
      setWithdrawing(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'warning',
      assigned: 'info',
      accepted: 'primary',
      in_progress: 'warning',
      verification_completed: 'success',
      approved: 'success',
      cancelled: 'default'
    };
    return colors[status] || 'default';
  };

  const getStatusLabel = (status) => {
    if (status === 'approved') return 'Payment Released';
    const labels = {
      pending: 'Pending Assignment',
      assigned: 'Verifier Assigned',
      accepted: 'Accepted',
      in_progress: 'In Progress',
      verification_completed: 'Awaiting Your Approval',
      approved: 'Approved',
      completed: 'Completed',
      cancelled: 'Withdrawn'
    };
    return labels[status] || status;
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4, textAlign: 'center' }}>
        <CircularProgress size={60} />
        <Typography variant="h6" sx={{ mt: 2 }}>Loading verification requests...</Typography>
      </Container>
    );
  }

  // Property checks and project verifications share this table; each row
  // routes to its own detail surface.
  const isPropertyCheckRow = (request) =>
    request.source === 'property_check' || String(request.id).startsWith('PC_');

  const visibleRequests = requests.filter((request) => {
    if (typeFilter === 'property') return isPropertyCheckRow(request);
    if (typeFilter === 'project') return !isPropertyCheckRow(request);
    return true;
  });

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" gutterBottom>
            Verifications
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Every verifier visit in one place — property checks and project work.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button variant="outlined" onClick={() => navigate('/property-check/book')} sx={{ textTransform: 'none', fontWeight: 600 }}>
            Book a Property Check
          </Button>
          <Button variant="contained" onClick={() => navigate('/verification-requests/create')} sx={{ textTransform: 'none', fontWeight: 600 }}>
            Verify Project Work
          </Button>
        </Box>
      </Box>

      <Box sx={{ mb: 3, display: 'flex', gap: 1 }}>
        <Chip label="All" color={typeFilter === 'all' ? 'primary' : 'default'} onClick={() => setTypeFilter('all')} />
        <Chip label="Property checks" color={typeFilter === 'property' ? 'primary' : 'default'} onClick={() => setTypeFilter('property')} />
        <Chip label="Project work" color={typeFilter === 'project' ? 'primary' : 'default'} onClick={() => setTypeFilter('project')} />
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {visibleRequests.length === 0 ? (
        <Paper sx={{ p: 6, textAlign: 'center' }}>
          <Assignment sx={{ fontSize: 80, color: 'text.secondary', opacity: 0.3, mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            {typeFilter === 'property' ? 'No property checks yet'
              : typeFilter === 'project' ? 'No project verifications yet'
              : 'No verifications yet'}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Book a property check to have a trusted verifier visit a property you own or are buying, or request project verification to check construction progress and milestone work.
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
            <Button variant="outlined" onClick={() => navigate('/property-check/book')} sx={{ textTransform: 'none' }}>
              Book a Property Check
            </Button>
            <Button variant="contained" onClick={() => navigate('/verification-requests/create')} sx={{ textTransform: 'none' }}>
              Verify Project Work
            </Button>
          </Box>
        </Paper>
      ) : (
        <Grid container spacing={3}>
          {visibleRequests.map(request => {
            const isPropertyCheck = isPropertyCheckRow(request);
            const openDetail = () => {
              if (isPropertyCheck) navigate(`/property-check/c/${request.id}`);
            };
            const stop = (handler) => (e) => {
              e.stopPropagation();
              handler?.(e);
            };
            return (
            <Grid item xs={12} key={request.id}>
              <Card
                elevation={2}
                onClick={isPropertyCheck ? openDetail : undefined}
                role={isPropertyCheck ? 'button' : undefined}
                tabIndex={isPropertyCheck ? 0 : undefined}
                onKeyDown={
                  isPropertyCheck
                    ? (e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          openDetail();
                        }
                      }
                    : undefined
                }
                sx={
                  isPropertyCheck
                    ? {
                        cursor: 'pointer',
                        transition: 'box-shadow 0.15s ease, transform 0.15s ease',
                        '&:hover': { boxShadow: 6, transform: 'translateY(-2px)' },
                        '&:focus-visible': {
                          outline: (theme) => `2px solid ${theme.palette.primary.main}`,
                          outlineOffset: 2,
                        },
                      }
                    : undefined
                }
              >
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 2 }}>
                    <Box>
                      <Typography variant="h6" gutterBottom>
                        {request.requestDetails?.title}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        {request.requestDetails?.description}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
                        <Chip
                          size="small"
                          label={getStatusLabel(request.status)}
                          color={getStatusColor(request.status)}
                        />
                        {request.is_open_to_bids && (
                          <Chip
                            size="small"
                            label="Open to Quotations"
                            color="info"
                          />
                        )}
                        {request.required_tier && (
                          <Chip
                            size="small"
                            label={`${request.required_tier} tier`}
                            variant="outlined"
                          />
                        )}
                        <Chip
                          size="small"
                          icon={<LocationOn />}
                          label={
                            request.location?.address ||
                            request.location?.name ||
                            [request.location?.city, request.location?.province]
                              .filter(Boolean)
                              .join(', ') ||
                            'No location'
                          }
                        />
                        <Chip
                          size="small"
                          icon={<AttachMoney />}
                          label={`$${request.payment?.verificationFee || request.estimated_fee || 50}`}
                        />
                      </Box>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1, flexDirection: 'column' }}>
                      {request.is_open_to_bids && request.proposal_count > 0 && (
                        <Button
                          variant="outlined"
                          color="primary"
                          size="small"
                          onClick={stop(() => navigate(`/verification-requests/${request.id}/proposals`))}
                        >
                          View Proposals ({request.proposal_count})
                        </Button>
                      )}
                      {request.status === 'verification_completed' && request.simpleVerificationId && (
                        <Button
                          variant="outlined"
                          color="primary"
                          startIcon={<Visibility />}
                          onClick={stop(() => navigate(`/simple-verifications/${request.simpleVerificationId}/report`))}
                        >
                          View Full Report
                        </Button>
                      )}
                      {request.status === 'verification_completed' && (
                        <Button
                          variant="contained"
                          color="success"
                          startIcon={<CheckCircle />}
                          onClick={stop(() => {
                            setSelectedRequest(request);
                            setApprovalDialog(true);
                          })}
                        >
                          Review & Approve
                        </Button>
                      )}
                      {['pending', 'assigned', 'accepted'].includes(request.status) && (
                        <Button
                          variant="outlined"
                          color="error"
                          size="small"
                          startIcon={<Cancel />}
                          onClick={stop(() => {
                            setSelectedRequest(request);
                            setWithdrawDialog(true);
                          })}
                        >
                          Withdraw
                        </Button>
                      )}
                    </Box>
                  </Box>

                  <Divider sx={{ my: 2 }} />

                  {/* Assigned Verifier Info */}
                  {request.verifier && (
                    <Box sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 2,
                      p: 2,
                      mb: 2,
                      bgcolor: 'success.50',
                      borderRadius: 1,
                      border: '1px solid',
                      borderColor: 'success.200'
                    }}>
                      <Avatar
                        src={request.verifier.profileImage}
                        sx={{ bgcolor: 'primary.main', width: 48, height: 48 }}
                      >
                        {request.verifier.name?.[0]?.toUpperCase() || 'V'}
                      </Avatar>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="subtitle2">
                          Assigned Verifier: {request.verifier.name}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Chip
                            size="small"
                            label={request.verifier.tier}
                            color={request.verifier.tier === 'professional' ? 'secondary' : 'default'}
                          />
                          {request.verifier.rating > 0 && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <Star fontSize="small" sx={{ color: 'gold' }} />
                              <Typography variant="body2">
                                {request.verifier.rating.toFixed(1)}
                              </Typography>
                            </Box>
                          )}
                          {request.verifier.acceptedQuoteAmount && (
                            <Chip
                              size="small"
                              icon={<AttachMoney />}
                              label={`Quote: $${request.verifier.acceptedQuoteAmount.toFixed(2)}`}
                              color="success"
                              variant="outlined"
                            />
                          )}
                        </Box>
                      </Box>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<Message />}
                        onClick={stop(() => navigate(`/messages?userId=${request.verifier.id}`))}
                      >
                        Message
                      </Button>
                    </Box>
                  )}

                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={3}>
                      <Typography variant="caption" color="text.secondary">
                        Project ID
                      </Typography>
                      <Typography variant="body2">
                        {request.projectId}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Typography variant="caption" color="text.secondary">
                        Request Type
                      </Typography>
                      <Typography variant="body2">
                        {request.source === 'standalone' ? 'Standalone verification' : (request.milestoneId || 'Project verification')}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Typography variant="caption" color="text.secondary">
                        Priority
                      </Typography>
                      <Typography variant="body2">
                        {request.priority}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Typography variant="caption" color="text.secondary">
                        Created
                      </Typography>
                      <Typography variant="body2">
                        {request.metadata?.createdAt ? 
                          formatDistanceToNow(new Date(request.metadata.createdAt)) + ' ago' :
                          'Unknown'
                        }
                      </Typography>
                    </Grid>
                  </Grid>

                  {request.verification?.verificationScore && (
                    <Box sx={{ mt: 2, p: 2, bgcolor: 'success.50', borderRadius: 1 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        Verification Score: {request.verification.verificationScore}/100
                      </Typography>
                      <LinearProgress 
                        variant="determinate" 
                        value={request.verification.verificationScore} 
                        sx={{ height: 8, borderRadius: 1 }}
                      />
                      {request.verification?.findings && (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                          {request.verification.findings}
                        </Typography>
                      )}
                    </Box>
                  )}

                  {/* Live Location Tracking - Show for in_progress verifications */}
                  {(request.status === 'in_progress' || request.status === 'accepted') && request.location && (
                    <Box sx={{ mt: 3 }} onClick={(e) => e.stopPropagation()}>
                      <VerifierLocationTracker
                        verificationRequestId={request.id}
                        verifierInfo={{
                          name: request.verifier?.name || 'Verifier',
                          phone: request.verifier?.phone
                        }}
                        projectLocation={{
                          lat: request.location.coordinates?.lat || request.location.lat,
                          lng: request.location.coordinates?.lng || request.location.lng
                        }}
                      />
                    </Box>
                  )}

                  {/* Workflow Progress */}
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="caption" color="text.secondary" gutterBottom>
                      Progress: {request.workflow?.currentStage}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 0.5, mt: 1 }}>
                      {request.workflow?.stages?.map((stage, idx) => (
                        <Box
                          key={idx}
                          sx={{
                            flex: 1,
                            height: 4,
                            bgcolor: stage.status === 'completed' ? 'success.main' : 'grey.300',
                            borderRadius: 1
                          }}
                        />
                      ))}
                    </Box>
                  </Box>

                  {isPropertyCheck && (
                    <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                      <Typography variant="body2" color="primary" sx={{ fontWeight: 600 }}>
                        {request.status === 'approved' || request.status === 'verification_completed'
                          ? 'View details & report →'
                          : 'View details →'}
                      </Typography>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>
            );
          })}
        </Grid>
      )}

      {/* Approval Dialog */}
      <Dialog open={approvalDialog} onClose={() => setApprovalDialog(false)} maxWidth="sm" fullWidth>
        <DialogCloseButton onClick={() => setApprovalDialog(false)} />
        <DialogTitle>Approve Verification</DialogTitle>
        <DialogContent>
          {selectedRequest && (
            <Box>
              <Typography variant="body2" gutterBottom>
                <strong>Verification Score:</strong> {selectedRequest.verification?.verificationScore}/100
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                {selectedRequest.verification?.findings}
              </Typography>

              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Rate this verifier
                </Typography>
                <Rating
                  value={rating}
                  onChange={(event, newValue) => setRating(newValue)}
                  size="large"
                />
              </Box>

              <TextField
                fullWidth
                multiline
                rows={4}
                label="Comments (optional)"
                value={comments}
                onChange={(e) => setComments(e.target.value)}
                placeholder="Add any feedback or comments..."
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApprovalDialog(false)}>Cancel</Button>
          <Button onClick={handleApprove} variant="contained" color="success">
            Approve Verification
          </Button>
        </DialogActions>
      </Dialog>

      {/* Withdraw Confirmation Dialog */}
      <Dialog open={withdrawDialog} onClose={() => setWithdrawDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Withdraw Verification Request?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            Are you sure you want to withdraw this verification request?
            {selectedRequest?.verifier && (
              <strong> The assigned verifier ({selectedRequest.verifier.name}) will be notified.</strong>
            )}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setWithdrawDialog(false)} disabled={withdrawing}>
            Cancel
          </Button>
          <Button
            onClick={handleWithdraw}
            variant="contained"
            color="error"
            disabled={withdrawing}
            startIcon={withdrawing ? <CircularProgress size={16} /> : <Cancel />}
          >
            {withdrawing ? 'Withdrawing...' : 'Withdraw Request'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ClientVerificationRequestsPage;
